import {
  Alert,
  Box,
  Container,
  Heading,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { Link as RouterLink } from "@tanstack/react-router"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FiArrowLeft, FiCheck, FiMail } from "react-icons/fi"
import { z } from "zod"

import { Button } from "@/components/atoms/Button"
import { Field } from "@/components/atoms/Field"
import { Input } from "@/components/atoms/Input"
import { InputGroup } from "@/components/atoms/InputGroup"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"

const resetSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

type ResetFormData = z.infer<typeof resetSchema>

export interface PasswordResetFormProps {
  onSuccess?: (email: string) => void
}

export const PasswordResetForm = ({ onSuccess }: PasswordResetFormProps) => {
  const [emailSent, setEmailSent] = useState(false)
  const [sentEmail, setSentEmail] = useState("")
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  })

  const resetMutation = useMutation({
    mutationFn: async (data: ResetFormData) => {
      const response =
        await api.auth.requestPasswordResetApiV1AuthPasswordResetPost({
          requestBody: { email: data.email },
        })
      return response.data
    },
    onSuccess: (_data, variables) => {
      setEmailSent(true)
      setSentEmail(variables.email)
      showSuccessToast("Password reset email sent successfully")
      onSuccess?.(variables.email)
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to send reset email: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const resendMutation = useMutation({
    mutationFn: async () => {
      const response =
        await api.auth.requestPasswordResetApiV1AuthPasswordResetPost({
          requestBody: { email: sentEmail },
        })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast("Password reset email sent again")
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to resend email: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const onSubmit = (data: ResetFormData) => {
    resetMutation.mutate(data)
  }

  const handleResend = () => {
    resendMutation.mutate()
  }

  if (emailSent) {
    return (
      <Container maxW="md" py={8}>
        <Stack gap={6}>
          {/* Success Header */}
          <Stack align="center" gap={3}>
            <Box p={3} borderRadius="full" bg="green.100" color="green.600">
              <FiCheck size={24} />
            </Box>
            <Heading size="lg" textAlign="center">
              Check Your Email
            </Heading>
            <Text color="gray.600" textAlign="center">
              We've sent a password reset link to
            </Text>
            <Text fontWeight="semibold" color="blue.600">
              {sentEmail}
            </Text>
          </Stack>

          {/* Instructions */}
          <Stack gap={4} p={6} bg="gray.50" borderRadius="lg">
            <Text fontSize="sm" color="gray.700" textAlign="center">
              Click the link in the email to reset your password. If you don't
              see the email, check your spam folder.
            </Text>

            <Alert.Root status="info" size="sm">
              <Alert.Indicator />
              <Alert.Description>
                The reset link will expire in 1 hour for security reasons.
              </Alert.Description>
            </Alert.Root>
          </Stack>

          {/* Action Buttons */}
          <Stack gap={3}>
            <Button
              variant="outline"
              onClick={handleResend}
              loading={resendMutation.isPending}
              w="full"
              gap={2}
            >
              <FiMail />
              Resend Email
            </Button>

            <RouterLink to="/login">
              <Button variant="ghost" w="full" gap={2}>
                <FiArrowLeft />
                Back to Login
              </Button>
            </RouterLink>
          </Stack>
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxW="md" py={8}>
      <Stack gap={6}>
        {/* Header */}
        <Stack align="center" gap={2}>
          <Heading size="lg">Reset Password</Heading>
          <Text color="gray.600" textAlign="center">
            Enter your email address and we'll send you a link to reset your
            password
          </Text>
        </Stack>

        {/* Reset Form */}
        <Box
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          p={6}
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
          shadow="sm"
        >
          <Stack gap={4}>
            {/* Email Field */}
            <Field
              label="Email Address"
              required
              invalid={!!errors.email}
              errorText={errors.email?.message}
            >
              <InputGroup startElement={<FiMail />}>
                <Input
                  {...register("email")}
                  type="email"
                  placeholder="Enter your email address"
                  autoFocus
                />
              </InputGroup>
            </Field>

            {/* Submit Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              loading={isSubmitting}
              w="full"
            >
              Send Reset Link
            </Button>
          </Stack>
        </Box>

        {/* Back to Login */}
        <Text textAlign="center" fontSize="sm" color="gray.600">
          Remember your password?{" "}
          <RouterLink to="/login">
            <Link color="blue.500" fontWeight="medium">
              Sign in here
            </Link>
          </RouterLink>
        </Text>

        {/* Security Notice */}
        <Alert.Root status="info" size="sm">
          <Alert.Indicator />
          <Alert.Description>
            For security reasons, we'll send the reset link even if the email
            address is not found in our system.
          </Alert.Description>
        </Alert.Root>
      </Stack>
    </Container>
  )
}

export default PasswordResetForm
